import Head from 'next/head'
import Link from 'next/link'
import Script from 'next/script'
import { Breadcrumb } from '@/commons/Breadcrumb'
import { buildBreadcrumb, breadcrumbJsonLd } from 'utils/breadcrumb'
import { organizationNode, localBusinessNode, WEBSITE_ID, SITE } from 'utils/local-business-schema'
import { legalName, uen, branchAddress, whatsAppDisplay, whatsAppUrl, googleRating, googleReviewCount, gbpUrl } from 'utils/business-info'
import { reviewedIso } from 'utils/article-date-time'

/**
 * "How StringsSG works" trust page for aircon, handyman and part-time helper.
 *
 * Voice: "we" (StringsSG, 2026-09-27). StringsSG defines the service steps and
 * quality standards for every segment and takes responsibility for the quality
 * of the service, so everyone delivering for StringsSG works the same way and
 * every client gets the same service. Customer copy never says "service
 * partner". Warranty terms are defined by /terms/warranty-claims.
 *
 * Personal training (in-house trainers) has /sg/personal-trainer/why-choose.
 * One company About page (/about-stringssg) plus one of these per segment,
 * rather than an "About Us" per segment that would repeat the others.
 */
export const HowStringsSGWorksPage = ({
    path,            // e.g. '/sg/handyman-singapore-services/how-stringssg-works'
    title,           // <title>
    h1,
    description,     // meta description
    reviewed,        // 'YYYY-MM-DD' - when the copy last actually changed
    intro,           // one paragraph: what this segment is
    serviceNoun,     // 'aircon job', 'handyman job', 'helper booking'
    people,          // 'technicians', 'handymen', 'helpers and cleaners'
    vetting,         // what we check before someone delivers for us
    standards,       // node: what the standardised steps cover for this segment
    includeSupplies = true,
    pricing,         // node: how prices are set, with a link to the price page
    afterService,    // node: satisfaction guarantee / warranty
    extraSections = [],
    bookHref,
    bookLabel,
}) => {
    const pageUrl = `${SITE}${path}`
    const trail = buildBreadcrumb(path, h1)
    const jsonLd = {
        '@context': 'https://schema.org',
        '@graph': [
            {
                '@type': 'AboutPage',
                '@id': `${pageUrl}/#webpage`,
                url: pageUrl,
                name: title,
                description,
                inLanguage: 'en-SG',
                isPartOf: { '@id': WEBSITE_ID },
                about: { '@id': organizationNode['@id'] },
                breadcrumb: { '@id': `${pageUrl}/#breadcrumblist` },
                dateModified: reviewedIso(reviewed),
            },
            breadcrumbJsonLd(trail, pageUrl),
            organizationNode,
            localBusinessNode,
        ],
    }
    const reviewedLabel = new Date(`${reviewed}T00:00:00+08:00`)
        .toLocaleDateString('en-SG', { day: 'numeric', month: 'long', year: 'numeric' })

    const sections = [
        {
            id: 'responsible',
            title: 'We are responsible for your service',
            body: (
                <>
                    <p>
                        When you book with StringsSG, StringsSG is responsible for the quality of the service you
                        receive. We define the service steps and quality standards for every {serviceNoun}, and
                        everyone who delivers for StringsSG follows them - so every customer gets the same service,
                        wherever they are in Singapore.
                    </p>
                    <p>
                        Before our {people} take StringsSG jobs, {vetting}. Once your booking is assigned you can
                        message them directly in the StringsSG app to confirm your address and access details, and the
                        whole exchange stays on record.
                    </p>
                </>
            ),
        },
        { id: 'standards', title: 'Our service standards', body: standards },
        {
            id: 'what-we-do',
            title: 'What we take care of',
            body: (
                <>
                    <p>So that our {people} can concentrate on the work itself, we run everything around it:</p>
                    <ul className="list-disc pl-6 space-y-1.5">
                        <li><strong>Service steps and quality standards</strong> - defined by StringsSG, the same on every job.</li>
                        <li><strong>Pricing</strong> - calculated and shown by StringsSG before you book.</li>
                        <li><strong>Booking and scheduling</strong> - appointments, and planning routes so less time is spent travelling between jobs.</li>
                        <li><strong>Invoicing and payment</strong> - billing runs through one central platform.</li>
                        {includeSupplies && <li><strong>Supplies</strong> - consolidated sourcing of materials.</li>}
                        <li><strong>Customer support</strong> - you contact StringsSG, and we make it right if something is not.</li>
                    </ul>
                </>
            ),
        },
        { id: 'pricing', title: 'How prices are set', body: pricing },
        { id: 'after-service', title: 'If something is not right', body: afterService },
        ...extraSections,
        {
            id: 'company',
            title: 'Company details',
            body: (
                <p>
                    {legalName}, UEN {uen}. {branchAddress.streetAddress}, Singapore {branchAddress.postalCode}.
                    Rated {googleRating} from {googleReviewCount} reviews on{' '}
                    <a href={gbpUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">Google</a>.
                    WhatsApp:{' '}
                    <a href={whatsAppUrl} target="_blank" rel="noopener noreferrer" className="text-orange-600 hover:underline">{whatsAppDisplay}</a>.{' '}
                    <Link href="/about-stringssg" prefetch={false} className="text-orange-600 hover:underline">About StringsSG</Link>
                </p>
            ),
        },
    ].filter((section) => section && section.body)

    return (
        <>
            <Head>
                <title>{title}</title>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                <meta name="description" content={description} />
                <meta name="author" content="StringsSG" />
                <meta property="og:locale" content="en_SG" />
                <meta property="og:url" content={pageUrl} />
                <meta property="og:type" content="website" />
                <meta property="og:title" content={title} />
                <meta property="og:description" content={description} />
                <meta property="og:site_name" content="StringsSG" />
                <meta name="twitter:card" content="summary" />
                <meta name="twitter:title" content={title} />
                <meta name="twitter:description" content={description} />
                <link rel="icon" href="/favicon.ico" />
                <link rel="canonical" href={pageUrl} />
                <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }} />
            </Head>

            <div className="max-w-3xl mx-auto px-4 pt-4 pb-16">
                <Breadcrumb pageTitle={h1} jsonLd={false} />

                <header className="py-8 space-y-3">
                    <h1 className="text-3xl md:text-4xl font-extrabold text-gray-900 tracking-tight">{h1}</h1>
                    <p className="text-gray-600 leading-7">{intro}</p>
                    <p className="text-xs text-gray-500">StringsSG · Last reviewed {reviewedLabel}</p>
                </header>

                <div className="space-y-10">
                    {sections.map((section) => (
                        <section key={section.id} aria-labelledby={section.id} className="space-y-3">
                            <h2 id={section.id} className="text-xl md:text-2xl font-bold text-gray-900">{section.title}</h2>
                            <div className="space-y-3 text-gray-700 leading-7">{section.body}</div>
                        </section>
                    ))}

                    <div className="pt-2">
                        <Link href={bookHref} prefetch={false}
                            className="inline-block rounded-xl bg-orange-600 text-white font-semibold px-6 py-3 hover:bg-orange-700">
                            {bookLabel}
                        </Link>
                    </div>
                </div>
            </div>

            <Script
                id="google-tagmanager-new"
                src="https://www.googletagmanager.com/gtag/js?id=G-QD1HGGHDYJ"
                strategy="lazyOnload"
                onLoad={() => {
                    window.dataLayer = window.dataLayer || [];
                    function gtag() { dataLayer.push(arguments); }
                    gtag('js', new Date());
                    gtag('config', 'G-QD1HGGHDYJ');
                }}
            />
        </>
    )
}
